import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Table, Button, Form } from 'react-bootstrap';
import { FaEdit, FaTrash, FaSave, FaTimes } from 'react-icons/fa';
import Select from 'react-select';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import SelectSearchInput from '../../SelectSearch';
import { toast } from 'react-toastify';

const AddUnit = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const [items, setItems] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [token, setToken] = useState(sessionStorage.getItem('token') || "");
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [editingIndex, setEditingIndex] = useState(null);

    const selectRef = useRef(null);
    const unitRef = useRef(null);
    const uomRef = useRef(null);

    const [formData, setFormData] = useState({
        name: '',
        rate: '',
        unit: '',
        uom: ''
    });

    useEffect(() => {
        const fetchStocks = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.post(`${baseUrl}/getstocksbygroup`, {
                    token: token,
                    outlet_name: selectedOutlet
                });
                setStocks(response.data);
            } catch (error) {
                console.error('Error fetching stocks:', error);
            }
        };

        fetchStocks();
    }, [token, selectedOutlet, baseUrl]);

    useEffect(() => {
        if (selectRef.current) {
            selectRef.current.focus();
        }
    }, [items]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleItemSelect = (selectedOption) => {
        const itemName = selectedOption ? selectedOption.value : '';
        const selectedItem = stocks
            .flatMap(group => group.stocks)
            .find(stock => stock.ItemName === itemName);

        if (selectedItem) {
            setFormData({
                name: selectedItem.ItemName,
                rate: selectedItem.Rate,
                unit: '',
                uom: ''
            });
        } else {
            setFormData({
                name: itemName,
                rate: '',
                unit: '',
                uom: ''
            });
        }

        setTimeout(() => {
            if (unitRef.current) {
                unitRef.current.focus();
            }
        }, 100);
    };

    const handleKeyDown = (e, nextRef) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            } else {
                handleAddItem(e);
            }
        }
    };

    const handleAddItem = (e) => {
        e.preventDefault();

        if (!formData.name || !formData.unit || !formData.uom) {
            alert('Please fill all required fields');
            return;
        }

        if (editingIndex !== null) {
            const updatedItems = [...items];
            updatedItems[editingIndex] = formData;
            setItems(updatedItems);
            setEditingIndex(null);
        } else {
            setItems([...items, formData]);
        }

        setFormData({
            name: '',
            rate: '',
            unit: '',
            uom: ''
        });

        setTimeout(() => {
            if (selectRef.current) {
                selectRef.current.focus();
            }
        }, 100);
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setFormData(items[index]);
        setTimeout(() => {
            if (unitRef.current) {
                unitRef.current.focus();
            }
        }, 100);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setFormData({
            name: '',
            rate: '',
            unit: '',
            uom: ''
        });
        setTimeout(() => {
            if (selectRef.current) {
                selectRef.current.focus();
            }
        }, 100);
    };

    const handleDelete = (index) => {
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
        if (editingIndex === index) {
            handleCancelEdit();
        }
    };

    const handleSave = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save these unit definitions?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (items.length === 0) {
                                alert('Please add at least one item');
                                return;
                            }

                            try {
                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    items: items.map(item => ({
                                        name: item.name,
                                        unit: parseFloat(item.unit),
                                        uom: item.uom,
                                        rate: parseFloat(item.rate)
                                    }))
                                };

                                await axios.post(`${baseUrl}/unit-definitions`, payload);
                                toast.success('Unit definitions saved successfully!');
                                setItems([]);
                            } catch (error) {
                                console.error('Error saving unit definitions:', error);
                                toast.error('Failed to save unit definitions');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-unit-definition'
            }
        );
    };

    const itemOptions = stocks
        .flatMap(group => group.stocks)
        .map(stock => ({
            value: stock.ItemName,
            label: stock.ItemName
        }));

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow'>
                <div className="max-w-5xl mx-auto mt-5 text-sm">
                    <div className="flex justify-center text-center mb-4">
                        <SelectSearchInput
                            token={token}
                            setToken={setToken}
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                        />
                    </div>

                    <Form onSubmit={handleAddItem} className="mb-8 bg-gray-50 p-4 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <Form.Group className="md:col-span-2">
                                <Form.Label>Item Name</Form.Label>
                                <Select
                                    name="name"
                                    value={formData.name ? { value: formData.name, label: formData.name } : null}
                                    onChange={handleItemSelect}
                                    ref={selectRef}
                                    options={itemOptions}
                                    placeholder="Select an item"
                                    required
                                    isSearchable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Label>Rate</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="rate"
                                    value={formData.rate}
                                    onChange={handleInputChange}
                                    readOnly
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Label>Unit</Form.Label>
                                <Form.Control
                                    type="number"
                                    name="unit"
                                    value={formData.unit}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) => handleKeyDown(e, uomRef)}
                                    required
                                    step="1"
                                    min="0"
                                    ref={unitRef}
                                />
                            </Form.Group>

                            <Form.Group>
                                <Form.Label>UOM</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="uom"
                                    value={formData.uom}
                                    onChange={handleInputChange}
                                    onKeyDown={(e) => handleKeyDown(e, null)}
                                    required
                                    ref={uomRef}
                                />
                            </Form.Group>
                        </div>

                        <div className="flex gap-2 mt-4">
                            <Button variant="primary" type="submit">
                                <div className='flex justify-center items-center gap-2'>
                                    {editingIndex !== null ? <FaSave /> : null}
                                    {editingIndex !== null ? 'Update Item' : 'Add Item'}
                                </div>
                            </Button>
                            {editingIndex !== null && (
                                <Button variant="secondary" onClick={handleCancelEdit}>
                                    <div className='flex justify-center items-center gap-2'>
                                        <FaTimes />
                                        Cancel
                                    </div>
                                </Button>
                            )}
                        </div>
                    </Form>

                    {items.length > 0 ? (
                        <div className="mb-8">
                            <Table striped bordered hover className='text-center'>
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Rate</th>
                                        <th>Unit</th>
                                        <th>UOM</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name}</td>
                                            <td>{item.rate}</td>
                                            <td>{item.unit}</td>
                                            <td>{item.uom}</td>
                                            <td>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleEdit(index)}
                                                    className="mr-2"
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(index)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                            <Button variant="success" onClick={handleSave}>
                                <div className='flex justify-center items-center gap-2'>
                                    Save All Items
                                </div>
                            </Button>
                        </div>
                    ) : (
                        <p className="text-gray-600 mb-8">No items added yet</p>
                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AddUnit;